import { Alert, Button, Group, Modal, Stack } from "@mantine/core";
import { useCallback, type FC } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { IconAlertTriangle } from "@tabler/icons-react";
import { useTRPC } from "../../../../trpc";
import { captureEvent } from "../../../../utils/posthog";
import { Event } from "../../../../constants/events";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Style } from "../types/map";

interface DeleteStyleModalProps {
  open: boolean;
  onClose: () => void;
  style: Style;
}

export const DeleteStyleModal: FC<DeleteStyleModalProps> = ({ onClose, open, style: { id, key } }) => {
  const intl = useIntl();
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutateAsync: deleteStyle, isPending } = useMutation(trpc.deleteStyle.mutationOptions());

  const handleDelete = useCallback(() => {
    captureEvent(Event.MapDeleteMap);
    void deleteStyle(id)
      .then(() => {
        void queryClient.invalidateQueries(trpc.getStylesWithCurrentVersion.queryFilter());
        onClose();
      })
      .catch((error: unknown) => {
        logger.error({ error }, "Failed to delete map");
      });
  }, [deleteStyle, id, onClose, queryClient]);

  return (
    <Modal
      opened={open}
      onClose={onClose}
      title={intl.formatMessage({ defaultMessage: "Delete map", id: "title.deleteMap" })}
    >
      <Stack gap="xs">
        <FormattedMessage
          id="text.deleteMapDescription"
          defaultMessage="Are you sure you want to delete the map {map}? This action cannot be undone."
          values={{ map: key }}
        />

        <Alert variant="light" color="red" icon={<IconAlertTriangle />}>
          <FormattedMessage
            defaultMessage="Deleting this map will remove it from your account and any applications using it may no longer display correctly."
            id="text.deleteMapWarning"
          />
        </Alert>

        <Group gap="xs" justify="flex-end" pt="lg">
          <Button variant="secondary" type="button" loading={isPending} onClick={onClose}>
            <FormattedMessage defaultMessage="Cancel" id="button.cancel" />
          </Button>

          <Button variant="danger" type="button" loading={isPending} onClick={handleDelete}>
            <FormattedMessage defaultMessage="Delete" id="button.delete" />
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
